import React, { useState } from 'react';
import { Button, Col, Drawer, Pagination, Row, Space, Spin, Table, Empty, Dropdown, Menu } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';

import { useScreenSize } from '../../../hooks/useSmallScreen';
import { ActionDefinition } from './new-crud-modal';

const { Column } = Table;

type NewTableProps<T> = {
  data?: T[];
  onRowClick?: (obj: T) => void;
  actions: ActionDefinition<T>[];

  groupingValue?: (obj: T) => any;
  groupingRender?: (obj: T) => React.ReactNode;

  renderBigScreen: React.ReactNode[];
  renderSmallScreen: (obj: T) => React.ReactNode;
  renderBorder?: (obj: T) => React.CSSProperties;

  total?: number;
  loading: boolean;
  pagination?: { pageSize: number; pageNumber: number };
  onPaginationChange?: ({ pageSize, pageNumber }: { pageSize: number; pageNumber: number }) => void;

  onHasMoreAfterClick?: () => void;
  onHasMoreBeforeClick?: () => void;
  hasMoreAfter?: string;
  hasMoreBefore?: string;
};

export function NewTable<T>(props: NewTableProps<T>) {
  const [actionsOpen, setActionOpen] = useState(false);
  const [obj, setObj] = useState<T | undefined>();

  const isSmallScreen = useScreenSize();

  const onPageChange = (pageNumber: number, pageSize: number) => {
    props.onPaginationChange!({ pageSize, pageNumber });
  };

  const openActionsDrawer = (obj: T) => {
    setActionOpen(true);
    setObj(obj);
  };

  const closeActionsDrawer = () => {
    setActionOpen(false);
    setObj(undefined);
  };

  const getBigTableActions = (obj: T) => {
    const actions = props.actions.filter((action) => action.canRender(obj));
    if (actions.length === 0) {
      return <></>;
    }
    const menu = (
      <Menu
        onClick={(menuInfo) => {
          menuInfo.domEvent.stopPropagation();
        }}
        items={actions.map((action) => {
          return {
            key: action.key,
            style: { width: '100%', textAlign: 'left' },
            danger: action.type === 'destructive',
            type: 'link',
            icon: action.icon,
            onClick: () => {
              action.handler(obj);
            },
            label: action.title,
          };
        })}
      />
    );
    return (
      <>
        <Dropdown overlay={menu}>
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Actions
          </Button>
        </Dropdown>
      </>
    );
  };

  const getSmallScreenActions = (obj?: T) => {
    if (!obj) {
      return <>No item selected.</>;
    }
    const actions = props.actions.filter((action) => action.canRender(obj));
    if (actions.length === 0) {
      return <>No actions to take.</>;
    }
    return actions.map((action) => {
      return (
        <Button
          key={action.key}
          style={{ width: '100%', textAlign: 'left' }}
          danger={action.type === 'destructive'}
          type="link"
          icon={action.icon}
          onClick={(e) => {
            e.stopPropagation();
            closeActionsDrawer();
            action.handler(obj);
          }}
        >
          {action.title}
        </Button>
      );
    });
  };

  const renderBigScreen = () => {
    return (
      <>
        <Table
          loading={props.loading}
          rowKey="id"
          dataSource={props.data as any} // hack because rc-table does not support generics
          pagination={false}
          className={`noBackground`}
          onRow={(record) => ({
            onClick: (e) => {
              e.stopPropagation();
              props.onRowClick && props.onRowClick(record);
            },
          })}
        >
          {props.renderBigScreen}
          <Column
            key="actions"
            align="right"
            render={(record: T) => {
              return <Space>{getBigTableActions(record)}</Space>;
            }}
          />
        </Table>
      </>
    );
  };

  const renderSmallScreen = () => {
    if (props.loading) {
      return <Spin style={{ width: '100%', padding: '20px' }} />;
    }

    if (props.data?.length === 0) {
      return <Empty style={{ backgroundColor: '#fff', margin: 0, padding: '32px' }} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
      <>
        {props.data?.map((i, index) => (
          <React.Fragment key={`list-item-${index}`}>
            {index === 0 && props.groupingValue && <>{props.groupingRender?.(i)}</>}
            {index !== 0 && props.groupingValue && props.groupingValue(i) !== props.groupingValue(props.data![index - 1]) && props.groupingRender?.(i)}
            <div
              style={{ backgroundColor: '#fff', padding: '20px 10px', borderBottom: '1px solid #e2e2e2', ...props.renderBorder?.(i) }}
              onClick={(e) => {
                e.stopPropagation();
                props.onRowClick && props.onRowClick(i);
              }}
            >
              <Row align="middle">
                <Col flex="auto">{props.renderSmallScreen(i)}</Col>
                <Col flex="10px">
                  <Button
                    type="text"
                    icon={<EllipsisOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openActionsDrawer(i);
                    }}
                  />
                </Col>
              </Row>
            </div>
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <>
      <Drawer title="Actions" placement="bottom" onClose={closeActionsDrawer} visible={actionsOpen} height="auto">
        <Space direction="vertical" style={{ width: '100%' }}>
          {getSmallScreenActions(obj!)}
        </Space>
      </Drawer>
      <div>
        {props.hasMoreBefore && (
          <Row justify="center" style={{ padding: '16px' }}>
            <Button disabled={props.loading} onClick={props.onHasMoreBeforeClick}>
              Load more
            </Button>
          </Row>
        )}
        {isSmallScreen ? renderSmallScreen() : renderBigScreen()}
        {!props.loading && props.pagination && props.total! > 0 && (
          <Row>
            <Col flex="auto" style={{ backgroundColor: '#fff', paddingBottom: '16px' }}>
              <Pagination style={{ marginTop: '16px' }} simple current={props.pagination.pageNumber} total={props.total} pageSize={props.pagination.pageSize} onChange={onPageChange} />
            </Col>
          </Row>
        )}
        {props.hasMoreAfter && (
          <Row justify="center" style={{ padding: '16px' }}>
            <Button disabled={props.loading} onClick={props.onHasMoreAfterClick}>
              Load more
            </Button>
          </Row>
        )}
      </div>
    </>
  );
}
